import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';

const fmtInr = (v) => `₹${Number(v || 0).toFixed(2)}`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, sessionId, clearCart, cartLoading } = useCart();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [settings, setSettings] = useState(null);
  const [shippingInfo, setShippingInfo] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    state: '',
    pincode: ''
  });

  useEffect(() => {
    if (orderPlaced) return;
    if (cartLoading) return;
    if (!cart?.items || cart.items.length === 0) {
      navigate('/cart');
      return;
    }
    fetchProductDetails();
    fetchSettings();
  }, [cart, cartLoading, orderPlaced]);

  const fetchProductDetails = async () => {
    try {
      const productIds = cart.items.map(item => item.product_id);
      const productPromises = productIds.map(id => api.get(`/products/${id}`));
      const responses = await Promise.all(productPromises);
      setProducts(responses.map(r => r.data));
    } catch (error) {
      console.error('Failed to fetch product details:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const r = await api.get('/settings/checkout');
      setSettings(r.data);
    } catch (e) { console.error('settings fetch failed', e); }
  };

  const calculateSubtotal = () => cart.items.reduce((sum, item) => {
    const product = products.find(p => p.id === item.product_id);
    return sum + (product ? product.price * item.quantity : 0);
  }, 0);

  const shippingCharge = (() => {
    if (!settings) return 0;
    const flat = Number(settings.shipping_flat_rate || 0);
    const freeAbove = Number(settings.shipping_free_above || 0);
    if (flat <= 0) return 0;
    if (freeAbove > 0 && calculateSubtotal() >= freeAbove) return 0;
    return flat;
  })();

  const totalPayable = calculateSubtotal() + shippingCharge;

  const handleInputChange = (e) => {
    setShippingInfo({ ...shippingInfo, [e.target.name]: e.target.value });
  };

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Create internal order (server recomputes pricing)
      const orderItems = cart.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity,
        customization: item.customization
      }));

      const orderParams = user ? {} : { session_id: sessionId };
      const orderResponse = await api.post('/orders/create', {
        items: orderItems,
        shipping_info: shippingInfo,
        guest_email: !user ? shippingInfo.email : null
      }, { params: orderParams });

      const order = orderResponse.data;

      // 2. Create Razorpay order linked to internal order id
      const paymentOrderResponse = await api.post(
        '/payment/create-order',
        { amount: order.total, currency: 'INR' },
        { params: { order_id: order.id } }
      );
      const razorpayOrder = paymentOrderResponse.data;

      // 3. Load Razorpay SDK
      const isLoaded = await loadRazorpayScript();
      if (!isLoaded) {
        toast.error('Failed to load Razorpay. Please check your connection.');
        setLoading(false);
        return;
      }

      // 4. Open Razorpay checkout
      // CRITICAL: use the key_id returned by the backend (razorpayOrder.key_id).
      // This guarantees the frontend key matches the account that created the order,
      // eliminating the "Something went wrong" mismatch class of bugs.
      const razorpayKey = razorpayOrder.key_id || process.env.REACT_APP_RAZORPAY_KEY_ID;
      if (!razorpayKey) {
        toast.error('Payment service is temporarily unavailable. Please try again shortly or contact support.');
        setLoading(false);
        return;
      }
      const options = {
        key: razorpayKey,
        amount: razorpayOrder.amount,
        currency: razorpayOrder.currency,
        name: 'Veveru',
        description: `Order ${order.order_number}`,
        order_id: razorpayOrder.id,
        handler: async (response) => {
          try {
            const verifyRes = await api.post('/payment/verify', {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_id: order.id
            });
            setOrderPlaced(true);
            await clearCart();
            toast.success('Payment successful!');
            navigate(`/orders/${order.id}?just_placed=1`);
          } catch (err) {
            const msg = err?.response?.data?.detail || 'Payment verification failed. Please contact support.';
            toast.error(msg);
            setLoading(false);
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.info('Payment cancelled. Your order is saved — you can retry from cart.');
          }
        },
        prefill: {
          name: shippingInfo.name,
          email: shippingInfo.email,
          contact: shippingInfo.phone
        },
        notes: { order_id: order.id, order_number: order.order_number },
        theme: { color: '#641b4d' }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        console.error('Razorpay payment failed:', response.error);
        toast.error(`Payment failed: ${response.error?.description || 'Please try again.'}`);
        setLoading(false);
      });
      razorpay.open();
    } catch (error) {
      console.error('Checkout error:', error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail || '';

      // Never surface raw Razorpay auth errors to end users.
      // 503 = our backend already returned the sanitized message.
      // 5xx or auth-related text from anywhere = show the same generic message.
      const looksAuth = /auth|unauthori[sz]ed|key_id|not authenticated/i.test(detail);
      let userMsg;
      if (status === 503 || looksAuth || (status >= 500 && status < 600)) {
        userMsg = 'Payment service is temporarily unavailable. Please try again shortly or contact support.';
      } else if (detail) {
        userMsg = `Checkout failed: ${detail}`;
      } else {
        userMsg = 'Failed to initiate payment. Please try again.';
      }
      toast.error(userMsg);
      setLoading(false);
    }
  };

  return (
    <div data-testid="checkout-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-4xl mx-auto px-6">
        <h1 data-testid="checkout-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-12">
          Checkout
        </h1>

        <form onSubmit={handlePayment} className="space-y-8">
          {/* Shipping Information */}
          <div className="bg-white border border-[#E5E2DC] p-8">
            <h2 className="text-2xl font-light mb-6">Shipping Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { name: 'name', label: 'Full Name *', type: 'text', span: false, testid: 'shipping-name-input' },
                { name: 'email', label: 'Email *', type: 'email', span: false, testid: 'shipping-email-input' },
                { name: 'phone', label: 'Phone *', type: 'tel', span: false, testid: 'shipping-phone-input' },
                { name: 'address', label: 'Address *', type: 'text', span: true, testid: 'shipping-address-input' },
                { name: 'city', label: 'City *', type: 'text', span: false, testid: 'shipping-city-input' },
                { name: 'state', label: 'State *', type: 'text', span: false, testid: 'shipping-state-input' },
                { name: 'pincode', label: 'Pincode *', type: 'text', span: false, testid: 'shipping-pincode-input' },
              ].map(f => (
                <div key={f.name} className={f.span ? 'md:col-span-2' : ''}>
                  <label htmlFor={f.name} className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">{f.label}</label>
                  <input
                    type={f.type}
                    id={f.name}
                    name={f.name}
                    data-testid={f.testid}
                    value={shippingInfo[f.name]}
                    onChange={handleInputChange}
                    required
                    className="input-underline w-full"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white border border-[#E5E2DC] p-8">
            <h2 className="text-2xl font-light mb-6">Order Summary</h2>
            <div className="space-y-4">
              {cart.items.map(item => {
                const product = products.find(p => p.id === item.product_id);
                if (!product) return null;
                return (
                  <div key={item.product_id} className="flex justify-between">
                    <span className="text-[#7a5e75]">{product.name} × {item.quantity}</span>
                    <span className="font-semibold">{fmtInr(product.price * item.quantity)}</span>
                  </div>
                );
              })}
              <div className="border-t border-[#E5E2DC] pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-[#7a5e75]">Product subtotal <span className="text-[10px] uppercase tracking-wider">(incl. GST)</span></span>
                  <span data-testid="checkout-subtotal">{fmtInr(calculateSubtotal())}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#7a5e75]">Shipping</span>
                  <span data-testid="checkout-shipping">{shippingCharge === 0 ? 'FREE' : fmtInr(shippingCharge)}</span>
                </div>
                <div className="pt-3 border-t border-[#E5E2DC] flex justify-between text-xl">
                  <span className="font-semibold">Total payable</span>
                  <span data-testid="checkout-total" className="font-semibold">{fmtInr(totalPayable)}</span>
                </div>
              </div>
            </div>
            {settings?.delivery_timeline && (
              <p className="mt-6 text-xs text-[#7a5e75] italic border-l-2 border-[#ffd034] pl-3 py-1">
                {settings.delivery_timeline}
              </p>
            )}
          </div>

          <button
            type="submit"
            data-testid="place-order-button"
            disabled={loading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Processing...' : `Place Order & Pay ${fmtInr(totalPayable)}`}
          </button>

          <p className="text-center text-xs text-[#7a5e75]">
            Payments are secured by Razorpay · HMAC-SHA256 verified
          </p>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
